<template>
  <div
    ref="refMeetingsSlots"
    class="right-part pt-md-4 calendar relative flex-b-fit d-flex align-center"
    :class="{
      'right-part-sm-and-down': smAndDown,
      'pt-4': !props.nextAppointment,
    }"
  >
    <div class="meeting-slots mx-auto">
      <!-- Desktop -->
      <vue-meeting-selector
        class="meeting-slots__meeting-selector mx-auto"
        v-model="selectedMeeting"
        :date="date"
        :loading="loading"
        :class-names="classNames"
        :calendar-options="meetingSlotsOptions(5)"
        :meetings-days="meetingsDays"
        @next-date="nextDate"
        @previous-date="previousDate"
        @update:modelValue="
          () =>
            onAppointmentClick(
              selectedMeeting.appointmentReasonId,
              selectedMeeting.remote,
            )
        "
      >
      </vue-meeting-selector>

      <div v-if="loading" class="meetings-days-wrapper__loading">
        <v-btn :loading="loading" icon variant="text"></v-btn>
      </div>

      <div
        v-if="!loading && noAvailability"
        class="meetings-days-wrapper__no-data"
      >
        {{ $t('practitionerPublicProfile.noAvailability') }}
      </div>

      <div
        v-if="nextAvailability"
        class="filter-calendar"
        :class="{ 'filter-calendar-sm-and-down': smAndDown }"
      >
        <v-btn
          color="blue"
          class="ma-2"
          :loading="loading"
          @click="prochaineDateFunc"
        >
          {{
            $t('searchPage.nextAppointmentDate', {
              date: nextAvailability,
            })
          }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, PropType, onMounted, watch } from 'vue'

/** For unknown reason these trigger "Cannot find module 'vue-meeting-selector' or its corresponding type declarations." error so just ts-ignore it to pass the build */
//@ts-ignore
import VueMeetingSelector from 'vue-meeting-selector'
import 'vue-meeting-selector/dist/style.css'

import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import { useAppointmentManagementStore } from '@/store/practitioner-public-profile/appointmentManagementStore'
import { meetingSlotsOptions } from '@/shared/meetingSlotsOptions'
import { useDisplay } from 'vuetify'
import { IPractitionerCard } from '@/store/search-page/mainSearchBarStore.d'
import MessageService from '../feedback/message/messageService'
import i18n from '@/plugins/i18n'
import LoadingBackdropService from '../feedback/loadingBackdrop/loadingBackdropService'
import createHttpClient from '@/api/httpClient'
import {
  AugmentedMeetingsDay,
  AvailabilityAppointment,
  transformData,
  addMissingDays,
  AugmentedMeetingSlot,
} from '../../shared/AppointmentHelper'
import { placeholderMeetingsDaysEmptyGenerator } from '@/utils/slotsGenerator'
import { goToProfile } from './SearchPageHelper'
import { ApiResponse } from '@/api/api'
import MeetingsDay from '../../../node_modules/vue-meeting-selector/src/interfaces/MeetingsDay.interface'
import MeetingSlot from '../../../node_modules/vue-meeting-selector/src/interfaces/MeetingSlot.interface'
import { useIntersectionObserver } from '@vueuse/core'
import { getDisciplineSlug } from '@/utils/discipline'

const NUMBER_OF_DAYS_ON_MOBILE = 2
const NUMBER_OF_DAYS_ON_DESKTOP = 4

dayjs.extend(weekday)
const { t } = i18n.global
const { smAndDown } = useDisplay()

const props = defineProps({
  nextAppointment: { type: String },
  practitionerData: { type: Object as PropType<IPractitionerCard> },
})

const appointmentManagementStore = useAppointmentManagementStore()
const numberOfDaysDisplayed = ref(NUMBER_OF_DAYS_ON_DESKTOP)

watch(smAndDown, async (newVal, _) => {
  if (newVal) {
    numberOfDaysDisplayed.value = NUMBER_OF_DAYS_ON_MOBILE
    await fetchAvailabilities()
  } else {
    numberOfDaysDisplayed.value = NUMBER_OF_DAYS_ON_DESKTOP
    await fetchAvailabilities()
  }
})

const mobile_appointmentMorning = computed(() => {
  return separerMeetingsParTemps(
    meetingsDays.value as AugmentedMeetingsDay[],
    start.value,
  ).matin
})

const mobile_appointmentAfternoon = computed(() => {
  return separerMeetingsParTemps(
    meetingsDays.value as AugmentedMeetingsDay[],
    start.value,
  ).apresMidi
})

interface DateMeetingOnMobile {
  date: String
  reasonId: string
  hasMeeting: Boolean
  index: Number
  remote: 'in-person' | 'remote'
}

interface SeparatedMeetings {
  matin: DateMeetingOnMobile[]
  apresMidi: DateMeetingOnMobile[]
}

/**
 * Function used for mobile view to separate between morning and afternoon
 *
 * */
function separerMeetingsParTemps(
  meetingsDays: AugmentedMeetingsDay[],
  start: dayjs.Dayjs = dayjs(),
): SeparatedMeetings {
  /**
   *  Function to return if there is a meeting in the morning given MeetingSlots[]
   * // dayjs uses always the user timezone by default (timezone of the system)
   * and this is what we want in all the application.
   * @param slots MeetingSlots[]
   */
  function hasMorningMeeting(slots: MeetingSlot[]) {
    return slots.some(
      slot => 12 > parseInt(dayjs(slot.date as string).format('HH')),
    )
  }

  /**
   *  Function to return if there is a meeting in the afternoon or night given MeetingSlots[]
   * @param slots MeetingSlots[]
   */
  function hasAfternoonMeeting(slots: MeetingSlot[]) {
    return slots.some(
      slot => 12 <= parseInt(dayjs(slot.date as string).format('HH')),
    )
  }

  /**
   * Function that returns the first reason id after the start date else null
   * @param meetingsDaysData
   * @param start
   */
  function getFirstReasonId(
    meetingsDaysData: AugmentedMeetingsDay[],
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
  ): string | null {
    if (end.isBefore(start)) return null
    for (const day of meetingsDaysData) {
      for (const slot of day.slots) {
        if (dayjs(slot.date).isAfter(start)) {
          return slot.appointmentReasonId
        }
      }
    }
    return null
  }

  /**
   * Function that returns the first reason id after the start date else null
   * @param meetingsDaysData
   * @param start
   */
  function getFirstRemote(
    meetingsDaysData: AugmentedMeetingsDay[],
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
  ): 'in-person' | 'remote' | null {
    if (end.isBefore(start)) return null
    for (const day of meetingsDaysData) {
      for (const slot of day.slots) {
        if (dayjs(slot.date).isAfter(start)) {
          return slot.remote
        }
      }
    }
    return null
  }

  const firstMorning: DateMeetingOnMobile = {
    index: 1,
    date: start.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStart = dateDay === start.format('YYYY-MM-DD')
      if (!isStart) return false

      return isStart && hasMorningMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(meetingsDays, start, start.hour(12)), // get first reason id of the morning
    remote: getFirstRemote(meetingsDays, start, start.hour(12)),
  }

  // Duplicated data on date due to design specifications
  const firstAfternoon: DateMeetingOnMobile = {
    index: 4,
    date: start.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStart = dateDay === start.format('YYYY-MM-DD')
      return isStart && hasAfternoonMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(
      meetingsDays,
      start.isBefore(dayjs().hour(12)) ? start.hour(11).minute(59) : start,
      start.hour(23).minute(59),
    ), // get first reason id of the afternoon
    remote: getFirstRemote(
      meetingsDays,
      start.isBefore(dayjs().hour(12)) ? start.hour(11).minute(59) : start,
      start.hour(23).minute(59),
    ),
  }

  const startPlusOne = dayjs()
    .add(1, 'day')
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
  const secondMorning: DateMeetingOnMobile = {
    index: 2,
    date: startPlusOne.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStartPlusOne = dateDay === startPlusOne.format('YYYY-MM-DD')

      return isStartPlusOne && hasMorningMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(
      meetingsDays,
      startPlusOne,
      startPlusOne.hour(11).minute(59),
    ), // get first reason id of the morning
    remote: getFirstRemote(
      meetingsDays,
      startPlusOne,
      startPlusOne.hour(11).minute(59),
    ),
  }
  // Duplicated data on date due to design specifications
  const secondAfternoon: DateMeetingOnMobile = {
    index: 5,
    date: startPlusOne.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStartPlusOne = dateDay === startPlusOne.format('YYYY-MM-DD')
      return isStartPlusOne && hasAfternoonMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(
      meetingsDays,
      startPlusOne.hour(11).minute(59),
      startPlusOne.hour(23).minute(59),
    ), // get first reason id of the afternoon
    remote: getFirstRemote(
      meetingsDays,
      startPlusOne.hour(11).minute(59),
      startPlusOne.hour(23).minute(59),
    ),
  }

  const startPlusTwo = dayjs()
    .add(2, 'day')
    .add(1, 'day')
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
  const thirdMorning: DateMeetingOnMobile = {
    index: 3,
    date: startPlusTwo.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStartPlusTwo = dateDay === startPlusTwo.format('YYYY-MM-DD')

      return isStartPlusTwo && hasMorningMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(
      meetingsDays,
      startPlusTwo,
      startPlusTwo.hour(23).minute(59),
    ), // get first reason id of the afternoon
    remote: getFirstRemote(
      meetingsDays,
      startPlusTwo,
      startPlusTwo.hour(23).minute(59),
    ),
  }
  // Duplicated data on date due to design specifications
  const thirdAfternoon: DateMeetingOnMobile = {
    index: 6,
    date: startPlusTwo.format('YYYY-MM-DD'),
    hasMeeting: meetingsDays.some(meetingsD => {
      const dateDay = dayjs(meetingsD.date as string).format('YYYY-MM-DD')
      const isStartPlusTwo = dateDay === startPlusTwo.format('YYYY-MM-DD')
      return isStartPlusTwo && hasAfternoonMeeting(meetingsD.slots)
    }),
    reasonId: getFirstReasonId(
      meetingsDays,
      startPlusTwo.hour(11).minute(59),
      startPlusTwo.hour(23).minute(59),
    ), // get first reason id of the afternoon
    remote: getFirstRemote(
      meetingsDays,
      startPlusTwo.hour(11).minute(59),
      startPlusTwo.hour(23).minute(59),
    ),
  }

  return {
    matin: [firstMorning, secondMorning, thirdMorning],
    apresMidi: [firstAfternoon, secondAfternoon, thirdAfternoon],
  }
}

const start = ref(dayjs())
const end = computed(() =>
  start.value
    .add(numberOfDaysDisplayed.value + 1, 'days')
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0),
)
const selectedMeeting = ref<AugmentedMeetingSlot>(null)
const loading = ref(false)
const date = computed(() => start.value.toDate())

// because of line-height, font-type you might need to change top value
const classNames = computed(() => ({
  tabLoading: 'loading-div',
}))

/**
 * If no arguments are given, this use the practitioner profile stored else all should be given
 * @param profileId
 * @param ownerId - practitioner id
 * @returns
 */
const fetchAppointmentSlotsFunc = async () => {
  if (!props.practitionerData) return
  const profileId = props.practitionerData?.id
  const addressesQuery = props.practitionerData.addresses
    .map(addr => addr.id)
    .join(',')

  loading.value = true
  await httpClient
    .get(
      `/appointment/availability?profiles=${profileId}&addresses=${addressesQuery}&start=${start.value.toISOString()}&end=${end.value.toISOString()}`,
    )
    .then(
      (
        response: ApiResponse<
          | NextAvailability
          | AvailabilityAppointment[]
          | {
              message: string
            }
        >,
      ) => {
        handleAvailibilityResponse(response)
      },
    )
    .catch(error => {
      console.error('Error fetching appointment slots:', error)
    })
    .finally(() => {
      loading.value = false
    })
}

const nextDate = async () => {
  start.value = start.value.add(numberOfDaysDisplayed.value, 'days')
  await fetchAppointmentSlotsFunc()
}

const previousDate = async () => {
  start.value = start.value.add(-numberOfDaysDisplayed.value, 'days')
  await fetchAppointmentSlotsFunc()
}

const prochaineDateFunc = async () => {
  start.value = nextAvailabilityDayjs.value
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
  await fetchAppointmentSlotsFunc()
}

const isCalendarActive = ref(false)

const httpClient = createHttpClient()

interface NextAvailability {
  date: Date
}
/**
 * This endpoint return AvailabilityAppointment[] or
 * {date} if the availabilities are existant after the range
 * or {message} if there is no availabilities
 */
const fetchAppointmentSlots = async () => {
  return await httpClient.get<
    AvailabilityAppointment[] | NextAvailability | { message: string }
  >(
    `/appointment/availability?profiles=${props.practitionerData.id}&addresses=${props.practitionerData.address.id}&start=${start.value.toISOString()}&end=${end.value.toISOString()}`,
  )
}

const meetingsDays = ref<MeetingsDay[] | AugmentedMeetingsDay[]>(
  placeholderMeetingsDaysEmptyGenerator(start.value),
)
const fetchDone = ref(false)
const noAvailability = ref<false | string>(false) // values : string with message or false
const nextAvailability = ref(null) // string of the next availability
const nextAvailabilityDayjs = ref<dayjs.Dayjs>(null) // D of the next availability

const setNextAvailabiility = (
  nextAvailabilityString: string,
  dateDayjs: dayjs.Dayjs,
) => {
  nextAvailability.value = nextAvailabilityString
  meetingsDays.value = placeholderMeetingsDaysEmptyGenerator(dayjs(), 4)
  noAvailability.value = false
  isCalendarActive.value = false
}

const setNoAvailabilityMessage = (message: string) => {
  noAvailability.value = message
  meetingsDays.value = placeholderMeetingsDaysEmptyGenerator(dayjs(), 4)
  nextAvailability.value = ''
  nextAvailabilityDayjs.value = null
  isCalendarActive.value = false
}

const setAvailabilities = data => {
  nextAvailability.value = ''
  nextAvailabilityDayjs.value = null
  noAvailability.value = false
  meetingsDays.value = data
  isCalendarActive.value = true
}

const handleAvailibilityResponse = (
  response: ApiResponse<
    | NextAvailability
    | AvailabilityAppointment[]
    | {
        message: string
      }
  >,
) => {
  const hasNoAvailability = Object.prototype.hasOwnProperty.call(
    response.data,
    'message',
  )
  const hasNextAvailability = Object.prototype.hasOwnProperty.call(
    response.data,
    'date',
  )
  if (hasNoAvailability) {
    // case no availability
    setNoAvailabilityMessage((response.data as any).message)
  } else if (hasNextAvailability) {
    // case there is an availability but after the range asked
    setNextAvailabiility(
      dayjs((response.data as any).date).format('ddd D MMM'),
      dayjs((response.data as any).date),
    )
    nextAvailabilityDayjs.value = dayjs((response.data as any).date)
  } else if (Array.isArray(response.data)) {
    // case we have the availabilities
    setAvailabilities(
      addMissingDays(
        transformData(response.data),
        start.value,
        numberOfDaysDisplayed.value,
      ),
    )
  }
}

const fetchAvailabilities = async () => {
  loading.value = true
  try {
    const responseAvailabilities = await fetchAppointmentSlots()
    handleAvailibilityResponse(responseAvailabilities)
  } catch (error) {
    console.error(error)
    MessageService.error(t('common.error.errorHasOccurred'))
  }
  loading.value = false
  LoadingBackdropService.stop()
}

defineExpose({
  fetchAvailabilities,
})

onMounted(async () => {
  if (smAndDown.value) {
    numberOfDaysDisplayed.value = NUMBER_OF_DAYS_ON_MOBILE
  }
})

const refMeetingsSlots = ref(null)
useIntersectionObserver(refMeetingsSlots, async ([{ isIntersecting }]) => {
  if (isIntersecting && !fetchDone.value) {
    await fetchAvailabilities()
    fetchDone.value = true
  }
})

const onAppointmentClick = async (
  selectedMeetingMotiveId: string = '',
  remote: 'in-person' | 'remote' | null = null,
) => {
  const nextAvailability = props.practitionerData.nextAppointment ?? ''

  appointmentManagementStore.selectedMeeting = selectedMeeting.value

  appointmentManagementStore.start = start.value

  if (
    (appointmentManagementStore.selectedMeeting &&
      dayjs(appointmentManagementStore.selectedMeeting.date).isAfter(
        start.value.add(
          appointmentManagementStore.numberOfDaysDisplayed,
          'days',
        ),
      )) ||
    (appointmentManagementStore.selectedMeeting &&
      dayjs(appointmentManagementStore.selectedMeeting.date).isSame(
        start.value.add(
          appointmentManagementStore.numberOfDaysDisplayed,
          'days',
        ),
      ))
  ) {
    appointmentManagementStore.start = dayjs(
      appointmentManagementStore.selectedMeeting.date,
    )
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
  }

  if (remote) {
    appointmentManagementStore.remote = remote
  }

  if (selectedMeetingMotiveId) {
    appointmentManagementStore.selectedReasonId = selectedMeetingMotiveId
  }

  await goToProfile(
    nextAvailability,
    props.practitionerData.address,
    props.practitionerData.mainDiscipline.slug,
    props.practitionerData.address.city,
    props.practitionerData.slug,
    true,
    selectedMeetingMotiveId,
    remote,
  )
}
</script>

<style scoped lang="scss">
.relative {
  position: relative;
}

.meeting-slots {
  height: 273px;
  position: relative;

  &__meeting-selector {
    max-width: 542px;
    min-width: 382px;
  }
}
// since our scss is scoped we need to use ::v-deep
// :deep(.loading-div) {
// top: 0px !important;
// }

.meetings-days-wrapper__no-data {
  position: absolute;
  top: 40px;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 90%;
  height: 90%;
  font-weight: bold;
}

.meetings-days-wrapper__no-data--sm-and-down {
  position: relative;
  top: 0;
  left: 0;
  transform: translate(0%, 0%);
}

.meetings-days-wrapper__loading {
  position: absolute;
  top: 50%;
  height: 100%;
  width: 100%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.filter-calendar {
  position: absolute;
  top: 30px;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
.filter-calendar-sm-and-down {
  position: absolute;
  top: 0px;
  display: flex;
  justify-content: center;
}

.right-part {
  width: 50%;
  flex-basis: 50%;
  align-self: stretch;
}

.right-part-sm-and-down {
  justify-content: center;
  margin: auto;
}

.meeting-slots-mobile__content {
  display: grid;
  grid-gap: 20px;
  grid-template-areas:
    'morning slot1 slot2 slot3'
    'afternoon slot4 slot5 slot6';

  & .morning {
    grid-area: morning;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  & .afternoon {
    grid-area: afternoon;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.meeting-slots-mobile__content-mobile-slot {
  border: 1px solid #000;
  width: 70px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;

  &.disabled {
    cursor: initial !important;
    opacity: 0.4;
  }

  &.slot1 {
    grid-area: slot1;
  }
  &.slot2 {
    grid-area: slot2;
  }
  &.slot3 {
    grid-area: slot3;
  }
  &.slot4 {
    grid-area: slot4;
  }
  &.slot5 {
    grid-area: slot5;
  }
  &.slot6 {
    grid-area: slot6;
  }
}

:deep(.meeting-selector) {
  .meeting__empty {
    padding: 6px 12px !important;
  }
  .day__title {
    font-size: 10.4px !important;
    line-height: 10.4px !important;
    font-weight: 400 !important;
    text-transform: capitalize !important;
    color: #616161 !important;
  }
  .day__subtitle {
    font-size: 10px !important;
    font-weight: 800 !important;
    text-transform: capitalize !important;
    color: #616161 !important;
  }
  .meeting__button {
    padding: 2px 2px !important;
    margin: 3px 3px !important;
    font-size: 12.46px !important;
    width: 44.08px !important;
    height: 44.08px !important;
    color: #1a73e9;
    background-color: #e9e9ff !important;
    font-weight: 800;
    border-radius: 8.19px;
  }
  .meeting__button:hover {
    background-color: #007aff !important;
    color: #f8f8f8 !important;
  }
  .meeting__button--selected {
    background-color: #007aff !important;
    color: #f8f8f8 !important;
  }
  .tab__day {
    border-bottom: none !important;
  }
  .vms-icon {
    font-size: 20px !important;
    color: rgb(var(--v-theme-secondary-darken-1)) !important;
  }
}

@media (max-width: 599px) {
  .meeting-slots {
    height: 273px;
    position: relative;

    &__meeting-selector {
      max-width: 542px;
      min-width: 315px !important;
    }
  }
}
</style>
